import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6" }
const _hoisted_2 = { class: "w-full max-w-md" }
const _hoisted_3 = { class: "bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8" }
const _hoisted_4 = { class: "flex justify-center mb-4" }
const _hoisted_5 = { class: "p-3 bg-[#22282F]/10 rounded-xl" }
const _hoisted_6 = {
  key: 0,
  class: "mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
}
const _hoisted_7 = { class: "text-sm text-red-800" }
const _hoisted_8 = {
  key: 1,
  class: "flex flex-col items-center justify-center py-10"
}
const _hoisted_9 = { class: "space-y-4 mb-6" }
const _hoisted_10 = { class: "flex gap-3" }
const _hoisted_11 = { class: "flex-1" }
const _hoisted_12 = { class: "flex justify-center" }
const _hoisted_13 = { class: "p-3 bg-white border border-gray-200 rounded-xl shadow-sm" }
const _hoisted_14 = ["src"]
const _hoisted_15 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_16 = {
  key: 0,
  class: "mt-2 flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
}
const _hoisted_17 = { class: "text-xs text-gray-800 break-all" }
const _hoisted_18 = { class: "flex justify-center mb-2" }
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { class: "flex items-center justify-center gap-2 sm:gap-3" }
const _hoisted_21 = ["value", "onInput", "onKeydown", "disabled"]
const _hoisted_22 = ["disabled"]
const _hoisted_23 = { key: 1 }

import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldCheck, AlertCircle, ArrowLeft, KeyRound, Copy } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'


export default /*@__PURE__*/_defineComponent({
  __name: 'TwoFactorSetupView',
  setup(__props) {

const store = useAuthStore()
const router = useRouter()

const qrCode = ref<string | null>(null)
const secret = ref<string | null>(null)
const showSecret = ref(false)
const loadingQr = ref(true)
const isLoading = ref(false)
const error = ref('')

const otp = ref<string[]>(Array(6).fill(''))
const otpRefs = ref<Array<HTMLInputElement | null>>([])
const isComplete = computed(() => otp.value.every((d) => /^\d$/.test(d)))

onMounted(async () => {
  // Accès direct sans flux de login forcé → retour login
  if (!store.setupToken) {
    router.replace('/')
    return
  }
  await loadQr()
})

async function loadQr() {
  loadingQr.value = true
  error.value = ''
  const result = await store.setup2FA()
  loadingQr.value = false
  if (result.success) {
    qrCode.value = result.qrCode || null
    secret.value = result.secret || null
    setTimeout(() => focusIndex(0), 50)
  } else {
    error.value = result.error || 'Impossible de générer le QR code'
  }
}

function setOtpRef(el: any, idx: number) {
  otpRefs.value[idx] = (el as HTMLInputElement) || null
}
function focusIndex(i: number) {
  const el = otpRefs.value[i]
  el?.focus()
  el?.select()
}
function distribute(digits: string) {
  const ds = digits.replace(/\D/g, '').slice(0, 6)
  for (let i = 0; i < 6; i++) otp.value[i] = ds[i] || ''
  const firstEmpty = otp.value.findIndex((d) => !d)
  focusIndex(firstEmpty === -1 ? 5 : firstEmpty)
}
function handleInput(e: Event, idx: number) {
  const target = e.target as HTMLInputElement
  const val = (target.value || '').replace(/\D/g, '')
  if (val.length > 1) { distribute(val); return }
  otp.value[idx] = val
  if (val && idx < 5) focusIndex(idx + 1)
}
function onKeydown(e: KeyboardEvent, idx: number) {
  if (e.key === 'Backspace') {
    if (otp.value[idx]) otp.value[idx] = ''
    else if (idx > 0) { otp.value[idx - 1] = ''; focusIndex(idx - 1) }
    e.preventDefault()
  } else if (e.key === 'ArrowLeft' && idx > 0) { focusIndex(idx - 1); e.preventDefault() }
  else if (e.key === 'ArrowRight' && idx < 5) { focusIndex(idx + 1); e.preventDefault() }
}
function onPaste(e: ClipboardEvent) {
  const digits = (e.clipboardData?.getData('text') || '').replace(/\D/g, '')
  if (!digits) return
  e.preventDefault()
  distribute(digits)
}
function resetCode() {
  otp.value = Array(6).fill('')
  focusIndex(0)
}

async function copySecret() {
  if (secret.value) {
    try { await navigator.clipboard.writeText(secret.value) } catch { /* noop */ }
  }
}

async function regenerateQr() {
  const ok = confirm('Générer un nouveau QR code invalidera l\'ancien. Votre application d\'authentification devra être reconfigurée. Continuer ?')
  if (!ok) return
  otp.value = Array(6).fill('')
  error.value = ''
  loadingQr.value = true
  const result = await store.setup2FA(true)
  loadingQr.value = false
  if (result.success) {
    qrCode.value = result.qrCode || null
    secret.value = result.secret || null
    setTimeout(() => focusIndex(0), 50)
  } else {
    error.value = result.error || 'Impossible de régénérer le QR code'
  }
}

async function confirm() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return
  error.value = ''
  isLoading.value = true
  try {
    const result = await store.confirm2FA(code)
    if (result.status === 'success') {
      router.push('/dashboard')
    } else if (result.status === 'expired') {
      error.value = (result as any).error
      setTimeout(() => router.replace('/'), 1500)
    } else {
      error.value = (result as any).error || 'Code incorrect'
      resetCode()
    }
  } catch {
    error.value = 'Erreur de connexion au serveur'
  } finally {
    isLoading.value = false
  }
}

function backToLogin() {
  router.replace('/')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-center mb-6 sm:mb-8" }, [
        _createElementVNode("div", { class: "flex justify-center mb-4" }, [
          _createElementVNode("img", {
            src: "/logo.png",
            alt: "Logo",
            class: "h-16 sm:h-20 w-auto object-contain"
          })
        ])
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(ShieldCheck), { class: "w-7 h-7 text-[#22282F]" })
          ])
        ]),
        _cache[10] || (_cache[10] = _createElementVNode("h2", { class: "text-xl sm:text-2xl font-bold text-gray-900 mb-1 text-center" }, " Configurer la double authentification ", -1)),
        _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-gray-500 text-sm text-center mb-6" }, " Pour sécuriser votre accès, activez la double authentification avant de continuer. ", -1)),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }),
              _createElementVNode("p", _hoisted_7, _toDisplayString(error.value), 1)
            ]))
          : _createCommentVNode("", true),
        (loadingQr.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, _cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mb-3" }, null, -1),
              _createElementVNode("p", { class: "text-gray-500 text-sm" }, "Génération du QR code…", -1)
            ])))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("ol", _hoisted_9, [
                _cache[5] || (_cache[5] = _createElementVNode("li", { class: "flex gap-3" }, [
                  _createElementVNode("span", { class: "flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center" }, "1"),
                  _createElementVNode("p", { class: "text-sm text-gray-700" }, " Installez une application d'authentification (Google Authenticator, Authy, Microsoft Authenticator…). ")
                ], -1)),
                _createElementVNode("li", _hoisted_10, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", { class: "flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center" }, "2", -1)),
                  _createElementVNode("div", _hoisted_11, [
                    _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-sm text-gray-700 mb-3" }, "Scannez ce QR code :", -1)),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        (qrCode.value)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: qrCode.value,
                              alt: "QR code 2FA",
                              class: "w-44 h-44"
                            }, null, 8, _hoisted_14))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    (secret.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                          _createElementVNode("button", {
                            type: "button",
                            onClick: _cache[0] || (_cache[0] = ($event: any) => (showSecret.value = !showSecret.value)),
                            class: "text-xs text-[#22282F] hover:underline flex items-center gap-1 mx-auto"
                          }, [
                            _createVNode(_unref(KeyRound), { class: "w-3.5 h-3.5" }),
                            _createTextVNode(" " + _toDisplayString(showSecret.value ? 'Masquer' : 'Saisie manuelle impossible ? Afficher la clé'), 1)
                          ]),
                          (showSecret.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _createElementVNode("code", _hoisted_17, _toDisplayString(secret.value), 1),
                                _createElementVNode("button", {
                                  type: "button",
                                  onClick: copySecret,
                                  class: "text-gray-400 hover:text-[#22282F]",
                                  title: "Copier"
                                }, [
                                  _createVNode(_unref(Copy), { class: "w-4 h-4" })
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _cache[6] || (_cache[6] = _createElementVNode("li", { class: "flex gap-3" }, [
                  _createElementVNode("span", { class: "flex-shrink-0 w-6 h-6 rounded-full bg-[#22282F] text-white text-xs font-semibold flex items-center justify-center" }, "3"),
                  _createElementVNode("p", { class: "text-sm text-gray-700" }, "Saisissez le code à 6 chiffres affiché par l'application :")
                ], -1))
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("button", {
                  type: "button",
                  onClick: regenerateQr,
                  disabled: isLoading.value || loadingQr.value,
                  class: "text-xs text-gray-400 hover:text-[#22282F] underline transition-colors disabled:opacity-40"
                }, " Le code ne fonctionne pas ? Générer un nouveau QR code ", 8, _hoisted_19)
              ]),
              _createElementVNode("form", {
                onSubmit: _withModifiers(confirm, ["prevent"]),
                class: "space-y-5"
              }, [
                _createElementVNode("div", _hoisted_20, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (_, i) => {
                    return _createElementVNode("input", {
                      key: i,
                      value: otp.value[i],
                      onInput: ($event: any) => (handleInput($event, i)),
                      onKeydown: ($event: any) => (onKeydown($event, i)),
                      onPaste: _cache[1] || (_cache[1] = ($event: any) => (onPaste($event))),
                      inputmode: "numeric",
                      autocomplete: "one-time-code",
                      pattern: "[0-9]*",
                      type: "tel",
                      disabled: isLoading.value,
                      class: "w-11 h-14 sm:w-12 sm:h-14 text-center text-lg font-semibold rounded-xl border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15 transition-all disabled:opacity-50",
                      ref_for: true,
                      ref: el => setOtpRef(el, i),
                      maxlength: "1"
                    }, null, 40, _hoisted_21)
                  }), 64))
                ]),
                _createElementVNode("button", {
                  type: "submit",
                  disabled: isLoading.value || !isComplete.value,
                  class: "w-full py-3 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#2a3139] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                }, [
                  (isLoading.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" }, null, -1)),
                        _cache[8] || (_cache[8] = _createElementVNode("span", null, "Activation…", -1))
                      ], 64))
                    : (_openBlock(), _createElementBlock("span", _hoisted_23, "Activer et continuer"))
                ], 8, _hoisted_22)
              ], 32)
            ], 64)),
        _createElementVNode("button", {
          type: "button",
          onClick: backToLogin,
          class: "mt-5 w-full text-sm text-gray-500 hover:text-[#22282F] transition-colors flex items-center justify-center gap-1"
        }, [
          _createVNode(_unref(ArrowLeft), { class: "w-4 h-4" }),
          _cache[9] || (_cache[9] = _createTextVNode(" Revenir à la connexion "))
        ])
      ]),
      _cache[13] || (_cache[13] = _createElementVNode("div", { class: "mt-6 text-center" }, [
        _createElementVNode("p", { class: "text-xs text-gray-400" }, "v1.0 • © 2025 Ministère des Affaires Étrangères")
      ], -1))
    ])
  ]))
}
}

})